import {
  GridActionsCellItem,
  GridColDef,
  GridRowId,
  GridRowModel,
  useGridApiRef,
} from "@mui/x-data-grid";
import { withApplicationInsights } from "../../components/telemetry";
import { QuickTaskEditingPanel } from "../../components/quickTaskEditingPanel";
import { ItemEditingPanelSimple } from "../../components/itemEditingPanelSimple";
import {
  MeetingItem,
  MeetingItemStatus,
  RiskIndicator,
  Task as CoachingTask,
} from "../../models";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import * as dashboardActions from "../../actions/actorDashboardActions";
import { bindActionCreators } from "../../actions/actionCreators";
import { AppContext } from "../../models/applicationState";
import { Context } from "../../components/context";
import { ItemActions } from "../../actions/itemActions";
import * as itemActions from "../../actions/itemActions";
import { saveQuickTask } from "../../actions/quickTaskActions";
import {
  Badge,
  Box,
  CssBaseline,
  Tooltip,
  useTheme,
  Snackbar,
  Alert,
  Chip,
  TextField,
  InputAdornment,
  IconButton,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import EditableGrid from "../../components/editableGrid";
import {
  LIBRARY_PAGE_HEADER_HEIGHT,
  LibraryPageHeader,
} from "../../components/libraryPageHeader";
import {
  MeetingItemRepresentation,
  categoryColumn,
  contentColumn,
  convertToRepresentation,
  createdDateColumn,
  creatorColumn,
  dueDateColumn,
  externalEmailMarkerColumn,
  getMeetingItemFromRepresentation,
  getRiskColor,
  hasRepresentationChanged,
  isoStringToDueDate,
  meetingNameColumn,
  overdueColumn,
  prioritizedColumn,
  riskColumn,
  statusColumn,
  statusTranslatedLabelOptions,
  riskTranslatedLabelOptions,
  priorityTranslatedLabelOptions,
  overdueTranslatedLabelOptions,
  priorityToTextConverter,
  overdueToTextConverter,
  isExternalEmailFromReference,
} from "../../models/meetingItemRepresentation";
import EditableDueDateCell from "../../components/editableDueDateCell";
import { useNavigate } from "react-router-dom";
import { ReactComponent as MeetingAttachment } from "../../icons/meeting_attachment.svg";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import { AttachmentsModal } from "../../components/attachmentsModal";
import ItemStatus from "../../components/itemStatus";
import { useTranslation } from "react-i18next";
import { TooltipIconWithBadge } from "../../components/tooltipIconWithBadge";
import CustomToolbar from "../../components/customToolbar";
import { ToolbarToggles } from "../../components/toolbarToggles";
import { ListActions } from "../../actions/listActions";
import * as listActions from "../../actions/listActions";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import {
  ItemEditingPanel,
  hasUserComments,
} from "../../components/itemEditingPanel";
import {
  addDueDateChangeCommentToItem,
  addRiskChangeCommentToItem,
  addContentChangeCommentToItem,
  addPrioritizationChangeCommentToItem,
  addStatusUpdateSystemCommentToItem,
  addActorChangeCommentToItem,
} from "../../components/itemEditingPanel";
import { TooltipIcon } from "../../components/tooltipIcon";
import EventNoteOutlinedIcon from "@mui/icons-material/EventNoteOutlined";
import { ReactComponent as AlarmIcon } from "../../icons/alarm_fill.svg";
import { useMsal } from "@azure/msal-react";
import ArchiveOutlinedIcon from "@mui/icons-material/ArchiveOutlined";
import UnarchiveIcon from "@mui/icons-material/Unarchive";
import PriorityHighIcon from "@mui/icons-material/PriorityHigh";
import FreeCheckboxSelectWithCount from "../../components/freeCheckboxSelectWithCount";
import ExternalEmailMarker from "../../components/externalEmailMarker";
import { generateExportConfig } from "../../util/exportConfigHelper";
import { TaskService } from "../../services/coachingNavigator/taskService";
import config from "../../config";
import { useGridSorting } from "../../util/useGridSorting";

const ActorDashboard = () => {
  const ACTOR_SORT_FIELD = "actor.sortField";
  const ACTOR_SORT_DIRECTION = "actor.sortDirection";
  const HIGHLIGHTED_ITEMS_KEY = "actorHighlightedItems";
  const navigate = useNavigate();
  const appContext = useContext<AppContext>(Context);
  const [items, setItems] = useState<MeetingItemRepresentation[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showCompleted, setShowCompleted] = useState(false);
  const [showInactive, setShowInactive] = useState(false);
  const [showOnlyPrioritized, setShowOnlyPrioritized] = useState(false);
  const [highlightedItemIds, setHghlightedItemIds] = useState<string[]>(
    JSON.parse(window.sessionStorage.getItem(HIGHLIGHTED_ITEMS_KEY)) || [],
  );
  const [showOnlySelfAssignedQuicktasks, setShowOnlySelfAssignedQuicktasks] =
    useState(false);
  const [showAttachmentsForMeeting, setShowAttachmentsForMeeting] =
    useState(undefined);
  const [showAttachmentsForItem, setShowAttachmentsForItem] =
    useState(undefined);
  const [showAttachmentsForTask, setShowAttachmentsForTask] =
    useState(undefined);
  const { t } = useTranslation();
  const [selectedProjectFilters, setSelectedProjectFilters] = useState(
    appContext.state.selectedProjectFilters,
  );
  const [showEditingPanelForItem, setShowEditingPanelForItem] =
    useState(undefined);
  const [showQuickTaskEditingPanel, setShowQuickTaskEditingPanel] =
    useState(undefined);
  const [showSimpleEditingPanel, setShowSimpleEditingPanel] =
    useState(undefined);
  const [showSuccessSnackbar, setShowSuccessSnackbar] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [debouncedSearchText, setDebouncedSearchText] = useState("");
  const theme = useTheme();
  const { instance } = useMsal();
  const gridApiRef = useGridApiRef();
  const [recentlyEditedRowId, setRecentlyEditedRowId] = useState<
    GridRowId | undefined
  >(undefined);
  const highlightTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    return () => {
      if (highlightTimeoutRef.current) {
        clearTimeout(highlightTimeoutRef.current);
        highlightTimeoutRef.current = null;
      }
    };
  }, []);

  // Export configuration
  const { exportFileName, customLines, printHeader } = generateExportConfig(
    t("menu.actor_dashboard"),
    t,
  );

  // Debounce search input - wait 300ms after user stops typing
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchText(searchText);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchText]);

  const actions = useMemo(
    () => ({
      items: bindActionCreators(
        itemActions,
        appContext.dispatch,
      ) as unknown as ItemActions,
      lists: bindActionCreators(
        listActions,
        appContext.dispatch,
      ) as unknown as ListActions,
    }),
    [appContext.dispatch],
  );
  const coachingTaskService = useMemo(
    () => new TaskService(config.api.baseUrl, "/coachingtasks"),
    [],
  );

  useEffect(() => {
    if (selectedProjectFilters !== appContext.state.selectedProjectFilters) {
      setSelectedProjectFilters(appContext.state.selectedProjectFilters);
      fetchData();
    }
  }, [
    appContext.state.selectedProjectFilters,
    selectedProjectFilters,
    navigate,
  ]);

  useEffect(() => {
    const existingStorage = window.sessionStorage.getItem(
      HIGHLIGHTED_ITEMS_KEY,
    );
    if (existingStorage) {
      const existingHighlightedItems = JSON.parse(existingStorage) as string[];
      if (existingHighlightedItems?.length !== highlightedItemIds?.length) {
        const stringToStore = JSON.stringify(highlightedItemIds);
        window.sessionStorage.setItem(HIGHLIGHTED_ITEMS_KEY, stringToStore);
      }
    } else {
      window.sessionStorage.setItem(
        HIGHLIGHTED_ITEMS_KEY,
        JSON.stringify(highlightedItemIds),
      );
    }
  }, [highlightedItemIds]);

  const gridItems = useMemo<MeetingItemRepresentation[]>(() => {
    let itemsToShow = items;
    // Filter by completion status
    if (showCompleted) {
      // Show ONLY completed tasks
      itemsToShow = itemsToShow.filter(
        (i) => i.status === MeetingItemStatus.Completed,
      );
    } else {
      // Hide completed tasks
      itemsToShow = itemsToShow.filter(
        (i) => i.status !== MeetingItemStatus.Completed,
      );
    }
    // Filter by inactive status (OnHold/Cancelled)
    if (showInactive) {
      // Show ONLY inactive tasks
      itemsToShow = itemsToShow.filter(
        (i) =>
          i.status === MeetingItemStatus.OnHold ||
          i.status === MeetingItemStatus.Cancelled,
      );
    } else {
      // Hide inactive tasks
      itemsToShow = itemsToShow.filter(
        (i) =>
          i.status !== MeetingItemStatus.OnHold &&
          i.status !== MeetingItemStatus.Cancelled,
      );
    }
    if (showOnlyPrioritized) {
      itemsToShow = itemsToShow.filter((i) => i.prioritized);
    }
    const hasProjectFilter =
      (selectedProjectFilters?.projectIds?.length ?? 0) > 0 ||
      (selectedProjectFilters?.tagIds?.length ?? 0) > 0;
    if (hasProjectFilter) {
      itemsToShow = itemsToShow.filter((i) => !!i.meetingId);
    }
    if (showOnlySelfAssignedQuicktasks) {
      itemsToShow = itemsToShow.filter(
        (i) => !i.meetingId && i.actor === i.creator,
      );
    }
    const todayAtNoon = new Date();
    todayAtNoon.setHours(12, 0, 0, 0);
    itemsToShow = itemsToShow.map((i) => ({
      ...i,
      overdue:
        i.status !== MeetingItemStatus.Completed &&
        i.status !== MeetingItemStatus.Cancelled &&
        i.dueDate
          ? new Date(i.dueDate) < todayAtNoon
          : false,
    }));
    // Apply search filter
    if (debouncedSearchText) {
      const lowerSearchText = debouncedSearchText.toLowerCase();
      itemsToShow = itemsToShow.filter(
        (i) =>
          i.category?.toLowerCase().includes(lowerSearchText) ||
          i.content?.toLowerCase().includes(lowerSearchText) ||
          i.actor?.toLowerCase().includes(lowerSearchText) ||
          i.meetingName?.toLowerCase().includes(lowerSearchText),
      );
    }
    return itemsToShow;
  }, [
    showCompleted,
    showInactive,
    items,
    showOnlyPrioritized,
    showOnlySelfAssignedQuicktasks,
    debouncedSearchText,
    selectedProjectFilters,
  ]);

  // Pre-sort gridItems using the custom hook — eliminates need for apiRef.applySorting()
  const { sortedRows, sortField, sortDirection, onSortModelChange } =
    useGridSorting({
      rows: gridItems,
      dateFields: ["dueDate", "createdDate"],
      localStorageFieldKey: ACTOR_SORT_FIELD,
      localStorageDirectionKey: ACTOR_SORT_DIRECTION,
    });

  // Fetch both meeting tasks and quicktasks, merge and display
  async function fetchData() {
    try {
      if (!showAttachmentsForMeeting && !showAttachmentsForItem) {
        const { state } = appContext;
        const actorEmail = state.invoker?.email;
        const hasProjectFilter =
          (state.selectedProjectFilters?.projectIds?.length ?? 0) > 0 ||
          (state.selectedProjectFilters?.tagIds?.length ?? 0) > 0;
        // Endpoint is actor-scoped on backend; keep full payload to avoid case-sensitivity drops.
        const meetingTasks = (await dashboardActions.list()) || [];
        // QuickTasks cannot be assigned to a project — exclude them when a project filter is active
        const quickTasks: MeetingItem[] = [];
        if (!hasProjectFilter) {
          const quickTaskActions = await import("../../actions/quickTaskActions");
          const quickTasksResult = await quickTaskActions.listQuickTasks(
            actorEmail,
            undefined,
          );
          const quickTasksArray = Array.isArray(quickTasksResult)
            ? quickTasksResult
            : [];
          quickTasks.push(
            ...quickTasksArray.filter(
              (task) => !task.meetingId && task.actor === task.creator,
            ),
          );
        }
        // Merge, filter out any tasks without an id, and remove duplicates by id
        const allTasks = [...meetingTasks, ...quickTasks];
        const validTasks = allTasks.filter((task) => !!task.id);
        // Remove duplicates by id
        const uniqueTasks = Object.values(
          validTasks.reduce(
            (acc: { [key: string]: MeetingItem }, task: MeetingItem) => {
              acc[task.id] = task;
              return acc;
            },
            {},
          ),
        );
        setItems(
          convertToRepresentation(uniqueTasks as MeetingItem[]).filter(
            (i) => !i.isSnapshot,
          ),
        );
      }
    } finally {
      // Dismiss the loader — EditableGrid handles the minimum display debounce.
      setIsLoading(false);
    }
  }

  const onArchived = (archived: boolean) => {
    onArchivedAsync(archived);
  };

  const onArchivedAsync = async (archived: boolean) => {
    if (!showAttachmentsForMeeting && !showAttachmentsForItem) {
      const response = await dashboardActions.list({ archived });
      setItems(convertToRepresentation(response).filter((i) => !i.isSnapshot));
    }
  };

  const onCompleted = (completed: boolean) => {
    onCompletedAsync(completed);
  };

  const onCompletedAsync = async (completed: boolean) => {
    setShowCompleted(completed);
  };

  const onInactive = (inactive: boolean) => {
    onInactiveAsync(inactive);
  };

  const onInactiveAsync = async (inactive: boolean) => {
    setShowInactive(inactive);
  };

  const onShowOnlyPrioritized = async (showOnlyPrioritized: boolean) => {
    onShowOnlyPrioritizedAsync(showOnlyPrioritized);
  };

  const onShowOnlyPrioritizedAsync = async (showOnlyPrioritized: boolean) => {
    setShowOnlyPrioritized(showOnlyPrioritized);
  };

  async function executeCallbackIfDiffsExist(callback?: () => Promise<void>) {
    if (!showAttachmentsForMeeting && !showAttachmentsForItem) {
      const response = await dashboardActions.listDiffs();
      if (response.length) {
        const highlightsToStore = highlightedItemIds?.length
          ? [...highlightedItemIds]
          : [];
        const newHighlightedIds = response.map((i) => i.id);
        if (newHighlightedIds) {
          for (const highlightedItemId of newHighlightedIds) {
            if (!highlightsToStore.includes(highlightedItemId)) {
              highlightsToStore.push(highlightedItemId);
            }
          }
          setHghlightedItemIds(highlightsToStore);
        }
        if (callback) {
          callback();
        }
      }
    }
  }

  useEffect(() => {
    executeCallbackIfDiffsExist();
    // Always fetch data on the initial load
    fetchData();
    const interval = setInterval(() => {
      executeCallbackIfDiffsExist(fetchData);
    }, 10000);
    return () => clearInterval(interval);
  }, [
    navigate,
    showAttachmentsForMeeting,
    showAttachmentsForItem,
    showAttachmentsForTask,
  ]);

  const onItemUpserted = async (item: MeetingItem) => {
    if (!item.meetingId) {
      return await saveQuickTask(item);
    }
    const savedItem = await actions.items.save(item.meetingId, item);
    return savedItem;
  };

  const handleStatusChange = (
    itemId: GridRowId,
    newStatus: MeetingItemStatus,
  ) => {
    const previousRow = items.find((row) => row.id === itemId);
    if (!previousRow || previousRow.status === newStatus) return;

    const userFullName =
      appContext.state.invoker.lastName && appContext.state.invoker.firstName
        ? appContext.state.invoker?.firstName +
          " " +
          appContext.state.invoker?.lastName
        : instance.getActiveAccount()?.name;

    const updatedRow: MeetingItemRepresentation = {
      ...previousRow,
      status: newStatus,
    };
    const isQuickTask = !updatedRow.meetingId;

    if (isQuickTask) {
      addStatusUpdateSystemCommentToItem(updatedRow, userFullName, t);
    } else {
      addStatusUpdateSystemCommentToItem(updatedRow, userFullName, t);
    }

    updatedRow.data = getMeetingItemFromRepresentation(updatedRow);
    if (isQuickTask) {
      (updatedRow.data as any).meetingId = undefined;
    }
    if ((updatedRow as any).comments) {
      (updatedRow.data as any).comments = (updatedRow as any).comments;
    }

    setItems((prev) =>
      prev.map((row) => (row.id === itemId ? updatedRow : row)),
    );
    onItemUpserted(updatedRow.data);

    // Set highlight for the edited row after sorting is applied
    if (highlightTimeoutRef.current) {
      clearTimeout(highlightTimeoutRef.current);
    }
    setRecentlyEditedRowId(itemId);
    highlightTimeoutRef.current = setTimeout(() => {
      setRecentlyEditedRowId(undefined);
    }, 1500);
  };

  const toCoachingTaskPayload = (
    row: MeetingItemRepresentation,
  ): CoachingTask | undefined => {
    if (!row.id || !row.clientId || !row.content || !row.status) {
      return undefined;
    }

    return {
      id: row.id,
      clientId: row.clientId,
      sessionId: row.sessionId,
      sessionName: row.sessionName || row.meetingName || "",
      content: row.content,
      category: row.category || "Coaching Hub",
      actor: row.actor,
      creator: row.creator,
      status: row.status as any,
      dueDate: row.dueDate ? new Date(row.dueDate) : undefined,
    };
  };

  const processRowUpdate = (
    newRow: GridRowModel<MeetingItemRepresentation>,
  ) => {
    const previousRow = items.find((row) => row.id === newRow.id);

    if (newRow.isCoachingTask) {
      if (!previousRow || previousRow.status === newRow.status) {
        return newRow;
      }

      const mergedRow = {
        ...previousRow,
        ...newRow,
      } as MeetingItemRepresentation;
      const payload = toCoachingTaskPayload(mergedRow);

      if (!payload) {
        console.warn("Skipping coaching task update due to incomplete payload");
        return previousRow;
      }

      setItems((prev) =>
        prev.map((row) => (row.id === mergedRow.id ? mergedRow : row)),
      );
      coachingTaskService.save(payload).catch((err) => {
        console.error("Failed to update coaching task status", err);
        setItems((prev) =>
          prev.map((row) => (row.id === previousRow.id ? previousRow : row)),
        );
      });
      return mergedRow;
    }
    if (!hasRepresentationChanged(newRow)) {
      return newRow;
    }
    const userFullName =
      appContext.state.invoker.lastName && appContext.state.invoker.firstName
        ? appContext.state.invoker?.firstName +
          " " +
          appContext.state.invoker?.lastName
        : instance.getActiveAccount()?.name;

    // Compare with previous row to add appropriate comments
    const isQuickTask = !newRow.meetingId;
    if (previousRow) {
      // Status
      if (previousRow.status !== newRow.status) {
        if (isQuickTask) {
          addStatusUpdateSystemCommentToItem(newRow, userFullName, t);
        } else {
          addStatusUpdateSystemCommentToItem(newRow, userFullName, t);
        }
      }
      // Due date
      if (previousRow.dueDate !== newRow.dueDate) {
        if (isQuickTask) {
          addDueDateChangeCommentToItem(
            newRow,
            userFullName,
            newRow.dueDate,
            t,
          );
        } else {
          addDueDateChangeCommentToItem(
            newRow,
            userFullName,
            newRow.dueDate,
            t,
          );
        }
      }
      if (previousRow.actor !== newRow.actor) {
        if (isQuickTask) {
          addActorChangeCommentToItem(
            newRow,
            userFullName,
            newRow.actor || "",
            t,
          );
        } else {
          addActorChangeCommentToItem(
            newRow,
            userFullName,
            newRow.actor || "",
            t,
          );
        }
      }
      newRow.data = getMeetingItemFromRepresentation(newRow);
      // Ensure quicktasks use quicktask route and carry comments
      if (isQuickTask) {
        (newRow.data as any).meetingId = undefined;
      }
      if ((newRow as any).comments) {
        (newRow.data as any).comments = (newRow as any).comments;
      }
      setItems((prev) =>
        prev.map((row) => (row.id === newRow.id ? newRow : row)),
      );
      // Save both meeting tasks and quicktasks inline
      onItemUpserted(newRow.data);

      // Set highlight for the edited row
      // Clear any existing timeout
      if (highlightTimeoutRef.current) {
        clearTimeout(highlightTimeoutRef.current);
      }
      setRecentlyEditedRowId(newRow.id);
      // Clear highlight after animation completes
      highlightTimeoutRef.current = setTimeout(() => {
        setRecentlyEditedRowId(undefined);
      }, 1500);

      return newRow;
    }
    return newRow;
  };
  // Only one definition, use !item.meetingId for quicktask detection
  const openItemEditingPanel = (item: MeetingItemRepresentation) => () => {
    if (!item.meetingId && item.creator === item.actor) {
      setShowQuickTaskEditingPanel({ item });
      return;
    }
    if (!item.meetingId) {
      setShowSimpleEditingPanel({ item });
      return;
    }
    setShowEditingPanelForItem({ item });
  };

  const onItemUpdated = async (
    itemRepresentation: MeetingItemRepresentation,
  ) => {
    if (itemRepresentation.isCoachingTask) {
      const payload = toCoachingTaskPayload(itemRepresentation);

      if (!payload) {
        console.warn("Skipping coaching task update due to incomplete payload");
        return;
      }

      setItems((prev) =>
        prev.map((row) =>
          row.id === itemRepresentation.id ? itemRepresentation : row,
        ),
      );

      try {
        await coachingTaskService.save(payload);
        setShowSuccessSnackbar(true);
      } catch (err) {
        console.error("Failed to update coaching task", err);
        fetchData();
      }
      return;
    }

    const userFullName =
      appContext.state.invoker.lastName && appContext.state.invoker.firstName
        ? appContext.state.invoker?.firstName +
          " " +
          appContext.state.invoker?.lastName
        : instance.getActiveAccount()?.name;

    // Find previous item for comparison
    const previousItem = items.find((row) => row.id === itemRepresentation.id);

    if (!itemRepresentation.meetingId) {
      // Quicktask: use comment functions
      if (previousItem && previousItem.status !== itemRepresentation.status) {
        addStatusUpdateSystemCommentToItem(itemRepresentation, userFullName, t);
      }
      if (previousItem && previousItem.dueDate !== itemRepresentation.dueDate) {
        addDueDateChangeCommentToItem(
          itemRepresentation,
          userFullName,
          itemRepresentation.dueDate,
          t,
        );
      }
      if (previousItem && previousItem.risk !== itemRepresentation.risk) {
        addRiskChangeCommentToItem(
          itemRepresentation,
          userFullName,
          itemRepresentation.risk,
          t,
        );
      }
      if (previousItem && previousItem.content !== itemRepresentation.content) {
        addContentChangeCommentToItem(itemRepresentation, userFullName, t);
      }
      if (
        previousItem &&
        previousItem.prioritized !== itemRepresentation.prioritized
      ) {
        addPrioritizationChangeCommentToItem(
          itemRepresentation,
          userFullName,
          itemRepresentation.prioritized,
          t,
        );
      }
      if (previousItem && previousItem.actor !== itemRepresentation.actor) {
        addActorChangeCommentToItem(
          itemRepresentation,
          userFullName,
          itemRepresentation.actor || "",
          t,
        );
      }
    } else {
      // Meeting task: use regular comment functions
      // Detect status change by comparing previous item's status to the new status
      if (previousItem && previousItem.status !== itemRepresentation.status) {
        addStatusUpdateSystemCommentToItem(itemRepresentation, userFullName, t);
      }
      if (previousItem && previousItem.dueDate !== itemRepresentation.dueDate) {
        addDueDateChangeCommentToItem(
          itemRepresentation,
          userFullName,
          itemRepresentation.dueDate,
          t,
        );
      }
      if (previousItem && previousItem.risk !== itemRepresentation.risk) {
        addRiskChangeCommentToItem(
          itemRepresentation,
          userFullName,
          itemRepresentation.risk,
          t,
        );
      }
      if (previousItem && previousItem.content !== itemRepresentation.content) {
        addContentChangeCommentToItem(itemRepresentation, userFullName, t);
      }
      if (
        previousItem &&
        previousItem.prioritized !== itemRepresentation.prioritized
      ) {
        addPrioritizationChangeCommentToItem(
          itemRepresentation,
          userFullName,
          itemRepresentation.prioritized,
          t,
        );
      }
      if (previousItem && previousItem.actor !== itemRepresentation.actor) {
        addActorChangeCommentToItem(
          itemRepresentation,
          userFullName,
          itemRepresentation.actor || "",
          t,
        );
      }
    }

    console.log("onItemUpdated:", itemRepresentation);
    setItems((prev) =>
      prev.map((row) =>
        row.id === itemRepresentation.id ? itemRepresentation : row,
      ),
    );
    // Persist changes for both meeting tasks and quicktasks
    const item = getMeetingItemFromRepresentation(itemRepresentation);
    if (!itemRepresentation.meetingId) {
      (item as any).meetingId = undefined;
      (item as any).comments = (itemRepresentation as any).comments;
    }
    await onItemUpserted(item);
    setShowSuccessSnackbar(true);
  };

  const openMeetingAttachments = (meetingId: string) => () => {
    setShowAttachmentsForMeeting(meetingId);
  };

  const openItemAttachments = (row: MeetingItemRepresentation) => () => {
    if (row.sessionId) {
      setShowAttachmentsForTask({ sessionId: row.sessionId, taskId: row.id });
      return;
    }
    setShowAttachmentsForItem({ id: row.id, meetingId: row.meetingId });
  };

  const onMeetingSelected = async (meetingId: string) => {
    actions.lists.load(meetingId);
  };

  const onNavigate = (meetingId: string) => {
    if (meetingId) {
      onMeetingSelected(meetingId);
      navigate(`/library/${meetingId}/current`);
    }
  };

  const handleClickNavigation = (item: MeetingItemRepresentation) => () => {
    onNavigate(item?.meetingId);
  };

  const onArchiveToggle = (itemRepresentation: MeetingItemRepresentation) => {
    itemRepresentation.archived = itemRepresentation.archived ? false : true;
    const item = getMeetingItemFromRepresentation(itemRepresentation);
    // Ensure quicktasks don't carry a placeholder meetingId so they use quicktask route
    if (!itemRepresentation.meetingId) {
      (item as any).meetingId = undefined;
      // Also carry over comments for quicktasks when archiving
      (item as any).comments = (itemRepresentation as any).comments;
    }
    setItems((prev) => prev.filter((row) => row.id !== itemRepresentation.id));
    onItemUpserted(item);
  };

  const toggleArchive =
    (itemRepresentation: MeetingItemRepresentation) => () => {
      onArchiveToggle(itemRepresentation);
    };

  const extraActions = (id: GridRowId, row: MeetingItemRepresentation) => {
    const isStandaloneTask = !row.meetingId;
    return [
      <GridActionsCellItem
        key="open"
        icon={
          <TooltipIconWithBadge
            titleKey={"general.open"}
            icon={<ChevronRightIcon />}
            badgeContent={hasUserComments(row, t) ? "i" : undefined}
          />
        }
        label={t("general.edit")}
        onClick={openItemEditingPanel(row)}
        showInMenu
      />,
      <GridActionsCellItem
        key="meetingAttachments"
        icon={<MeetingAttachment />}
        label={t("dashboard.meetingAttachments")}
        onClick={openMeetingAttachments(row.meetingId)}
        disabled={isStandaloneTask}
        showInMenu
      />,
      <GridActionsCellItem
        key="itemAttachments"
        icon={<AttachFileIcon />}
        label={t("dashboard.itemAttachments")}
        onClick={openItemAttachments(row)}
        showInMenu
      />,
      <GridActionsCellItem
        key="openMeeting"
        icon={<EventNoteOutlinedIcon />}
        label={t("meeting.open")}
        onClick={handleClickNavigation(row)}
        disabled={isStandaloneTask}
        showInMenu
      />,
      <GridActionsCellItem
        key="archive"
        icon={row.archived ? <UnarchiveIcon /> : <ArchiveOutlinedIcon />}
        label={row.archived ? t("general.unarchive") : t("general.archive")}
        disabled={row.creator !== instance.getActiveAccount()?.username}
        onClick={toggleArchive(row)}
        showInMenu
      />,
    ];
  };

  const columns: GridColDef[] = [
    {
      ...riskColumn,
      type: "singleSelect",
      valueFormatter: (value: string) => t(`item_risk.${value}`),
      valueOptions: riskTranslatedLabelOptions(t),
      renderCell: (params) => (
        <Box
          sx={{
            backgroundColor: getRiskColor(params.row.risk),
            width: "auto",
            height: "32px",
            margin: "10px",
          }}
        ></Box>
      ),
    },
    {
      ...createdDateColumn,
      renderCell: (params) => (
        <Badge
          color="primary"
          badgeContent=" "
          variant="dot"
          sx={{ display: "inline" }}
          invisible={!highlightedItemIds.includes(params.row.id)}
          anchorOrigin={{
            vertical: "top",
            horizontal: "left",
          }}
        >
          <span>{createdDateColumn.valueFormatter(params.value)}</span>
        </Badge>
      ),
    },
    { ...meetingNameColumn, width: 150 },
    categoryColumn,
    { ...contentColumn, flex: 0.6, cellClassName: "content-cell" },
    {
      ...(prioritizedColumn as GridColDef),
      type: "singleSelect",
      valueFormatter: (value: boolean) => priorityToTextConverter(value),
      valueOptions: priorityTranslatedLabelOptions(t),
      filterOperators: [
        {
          label: t("contains"),
          value: "contains",
          getApplyFilterFn: (filterItem) => {
            if (!filterItem.value || filterItem.value.length === 0) {
              return null;
            }

            return (value) => {
              if (value === undefined || value === null) return false;
              {
                showQuickTaskEditingPanel && (
                  <QuickTaskEditingPanel
                    meetingItem={showQuickTaskEditingPanel.item}
                    open={true}
                    allowMetadataEditing={true}
                    allowAttachmentsEditing={true}
                    onClose={() => setShowQuickTaskEditingPanel(undefined)}
                    onUpdate={onItemUpdated}
                  />
                );
              }
              // Compare the primitive boolean value directly
              return filterItem.value.some((item) => item.value === value);
            };
          },
          InputComponent: (props) => (
            <FreeCheckboxSelectWithCount
              options={priorityTranslatedLabelOptions(t)}
              values={props.item.value || []}
              onValueSelected={(newValue) => {
                props.applyValue({ ...props.item, value: newValue });
              }}
              placeholder={t("general.select")}
              label={t("item.priority_selection_label")}
            />
          ),
        },
      ],
      renderCell: (params) => (
        <Box>
          {params.row.prioritized && (
            <Tooltip title={t("item.prioritized")}>
              <span>
                {params.row.dueDate && params.row.overdue ? (
                  <PriorityHighIcon
                    sx={{
                      color: theme.palette.error.main,
                    }}
                  />
                ) : (
                  <PriorityHighIcon />
                )}
              </span>
            </Tooltip>
          )}
        </Box>
      ),
    },
    {
      ...(overdueColumn as GridColDef),
      type: "singleSelect",
      valueFormatter: (value: boolean) => overdueToTextConverter(value),
      valueOptions: overdueTranslatedLabelOptions(t),
      filterOperators: [
        {
          label: t("contains"),
          value: "contains",
          getApplyFilterFn: (filterItem) => {
            if (!filterItem.value || filterItem.value.length === 0) {
              return null;
            }

            return (value) => {
              if (value === undefined || value === null) return false;
              // Compare the primitive boolean value directly
              return filterItem.value.some((item) => item.value !== value);
            };
          },
          InputComponent: (props) => (
            <FreeCheckboxSelectWithCount
              options={overdueTranslatedLabelOptions(t)}
              values={props.item.value || []}
              onValueSelected={(newValue) => {
                props.applyValue({ ...props.item, value: newValue });
              }}
              placeholder={t("general.select")}
              label={t("item.priority_selection_label")}
            />
          ),
        },
      ],
      renderCell: (params) => (
        <Box>
          {params.row.overdue && (
            <Tooltip title={t("item.overdue")}>
              <span>
                <AlarmIcon color={theme.palette.error.main} />
              </span>
            </Tooltip>
          )}
        </Box>
      ),
    },
    // {
    //     ...overdueColumn as GridColDef,
    //     renderCell: (params) => (
    //         <Box>
    //             {params.row.overdue
    //                 && (
    //                     <Tooltip title={t('item.overdue')}>
    //                         <span>
    //                             <AlarmIcon color={theme.palette.error.main} />
    //                         </span>
    //                     </Tooltip>
    //                 )}
    //         </Box>
    //     ),
    // },
    {
      ...dueDateColumn,
      editable: false,
      minWidth: 145,
      maxWidth: 145,
      renderCell: (params) => (
        <EditableDueDateCell
          taskId={String(params.row.id)}
          dueDate={params.row.dueDate}
          onChange={(_taskId, newISODate) => {
            const newRow: MeetingItemRepresentation = {
              ...params.row,
              dueDate: isoStringToDueDate(newISODate) as Date,
            };
            if (!newRow.task) newRow.task = true;
            processRowUpdate(newRow);
          }}
        />
      ),
    },
    {
      ...statusColumn,
      editable: false,
      type: "singleSelect",
      valueFormatter: (value: string) => t(`item_status.${value}`),
      valueOptions: statusTranslatedLabelOptions(t),
      renderCell: (params) => (
        <ItemStatus
          status={params.row.status}
          lastModifiedBy={params.row.statusLastUpdatedBy}
          onChange={(newStatus) => handleStatusChange(params.row.id, newStatus)}
        />
      ),
    },
    {
      ...creatorColumn,
      flex: 0.2,
      minWidth: 180,
      renderCell: (params) => {
        return (
          <Box sx={{ display: "flex", width: "100%" }}>
            {params.row.creator === params.row.actor ? (
              <Chip
                label={t("item.quicktask.self_assigned_label")}
                size="small"
                color="primary"
                sx={{ mt: "15px", fontSize: "0.7rem", maxWidth: "100%" }}
              />
            ) : (
              <Tooltip title={params.row.creator} placement="top">
                <Chip
                  // Display full name if available, otherwise format from email or show as is
                  label={
                    params.row.creator.firstName
                      ? `${params.row.creator.firstName} ${params.row.creator.lastName}`
                      : params.row.creator
                  }
                  size="small"
                  sx={{
                    mt: "15px",
                    fontSize: "0.7rem",
                    backgroundColor: "#777586",
                    color: "#fff",
                    maxWidth: "100%",
                    "& .MuiChip-label": {
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    },
                  }}
                />
              </Tooltip>
            )}
          </Box>
        );
      },
    },
    {
      // Show yellow "E" marker after actor email for external addresses
      ...externalEmailMarkerColumn,
      renderCell: (params) => {
        const creator: string | undefined = params.row.creator;
        return (
          <ExternalEmailMarker email={creator} internalFromEmail={creator} />
        );
      },
    },
    // meetingAttachmentsColumn,
    // itemAttachmentsColumn,
  ];

  return (
    <Box
      sx={{
        pt: 2,
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <CssBaseline />
      <LibraryPageHeader
        title={t("menu.actor_dashboard")}
        chipLabel={t("general.task", { count: gridItems.length })}
        rightContent={
          <>
            <ToolbarToggles
              onArchived={onArchived}
              onCompleted={setShowCompleted}
              onInactive={setShowInactive}
              onShowOnlyPrioritized={setShowOnlyPrioritized}
              onShowOnlySelfAssignedQuicktasks={
                setShowOnlySelfAssignedQuicktasks
              }
            />
            <TextField
              size="small"
              placeholder={t("search.dashboard_placeholder")}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              sx={{
                width: 250,
                "& .MuiOutlinedInput-root": {
                  backgroundColor: "white",
                },
              }} slotProps={{ input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon
                      sx={{ color: "text.secondary", fontSize: "1.25rem" }}
                    />
                  </InputAdornment>
                ),
                endAdornment: searchText && (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => setSearchText("")}
                      edge="end"
                      sx={{ padding: "4px" }}
                    >
                      <ClearIcon sx={{ fontSize: "1.25rem" }} />
                    </IconButton>
                  </InputAdornment>
                ),
              } }}
            />
          </>
        }
        exportProps={{
          apiRef: gridApiRef,
          csvOptions: {
            customLines,
            exportFileName,
          },
          printOptions: {
            printHeader,
            hiddenColumns: [t("item.risk"), t("meeting.actions"), null, "ID"],
          },
        }}
      />
      {showAttachmentsForMeeting && (
        <AttachmentsModal
          open={true}
          allowModifications={true}
          height="70vh"
          uploadPrefix={`meetings/${showAttachmentsForMeeting}/`}
          meetingId={showAttachmentsForMeeting}
          onClose={() => setShowAttachmentsForMeeting(undefined)}
          onAttachmentsCountChanged={(count, meetingId, _itemId) =>
            setItems((prev) =>
              prev.map((item) =>
                item.meetingId === meetingId
                  ? { ...item, meetingAttachments: count }
                  : item,
              ),
            )
          }
        />
      )}
      {showEditingPanelForItem && (
        <ItemEditingPanel
          allowMetadataEditing={true}
          allowAttachmentsEditing={true}
          meetingItem={showEditingPanelForItem.item}
          open={true}
          onClose={() => setShowEditingPanelForItem(undefined)}
          onUpdate={onItemUpdated}
          onAttachmentsCountChanged={(count, _meetingId, itemId) =>
            setItems((prev) =>
              prev.map((item) =>
                item.id === itemId ? { ...item, attachments: count } : item,
              ),
            )
          }
        />
      )}
      {showSimpleEditingPanel && (
        <ItemEditingPanelSimple
          meetingItem={showSimpleEditingPanel.item}
          open={true}
          allowMetadataEditing={true}
          allowAttachmentsEditing={true}
          onClose={() => setShowSimpleEditingPanel(undefined)}
          onUpdate={onItemUpdated}
          onAttachmentsCountChanged={(count, _meetingId, itemId) =>
            setItems((prev) =>
              prev.map((item) =>
                item.id === itemId ? { ...item, attachments: count } : item,
              ),
            )
          }
        />
      )}
      {showQuickTaskEditingPanel && (
        <QuickTaskEditingPanel
          meetingItem={showQuickTaskEditingPanel.item}
          open={true}
          allowMetadataEditing={true}
          allowAttachmentsEditing={true}
          onClose={() => setShowQuickTaskEditingPanel(undefined)}
          onUpdate={onItemUpdated}
          onAttachmentsCountChanged={(count, _meetingId, itemId) =>
            setItems((prev) =>
              prev.map((item) =>
                item.id === itemId ? { ...item, attachments: count } : item,
              ),
            )
          }
          onDelete={(deletedItem) => {
            setItems((items) =>
              items.filter((item) => item.id !== deletedItem.id),
            );
            setShowQuickTaskEditingPanel(undefined);
          }}
        />
      )}
      {showAttachmentsForItem && (
        <AttachmentsModal
          open={true}
          allowModifications={true}
          height="70vh"
          uploadPrefix={
            showAttachmentsForItem.meetingId
              ? `items/meetings/${showAttachmentsForItem.meetingId}/items/${showAttachmentsForItem.id}/`
              : `items/quicktasks/${showAttachmentsForItem.id}/`
          }
          meetingId={showAttachmentsForItem.meetingId}
          itemId={showAttachmentsForItem.id}
          onClose={() => setShowAttachmentsForItem(undefined)}
          onAttachmentsCountChanged={(count, _meetingId, itemId) =>
            setItems((prev) =>
              prev.map((item) =>
                item.id === itemId ? { ...item, attachments: count } : item,
              ),
            )
          }
        />
      )}
      {showAttachmentsForTask && (
        <AttachmentsModal
          open={true}
          allowModifications={true}
          height="70vh"
          uploadPrefix={`sessions/${showAttachmentsForTask.sessionId}/tasks/${showAttachmentsForTask.taskId}/`}
          sessionId={showAttachmentsForTask.sessionId}
          taskId={showAttachmentsForTask.taskId}
          onClose={() => setShowAttachmentsForTask(undefined)}
          onAttachmentsCountChanged={(
            count,
            _meetingId,
            _itemId,
            sessionId,
            taskId,
          ) =>
            setItems((prev) =>
              prev.map((item) =>
                item.sessionId === sessionId && item.id === taskId
                  ? { ...item, attachments: count }
                  : item,
              ),
            )
          }
        />
      )}
      <Box sx={{ flexGrow: 1 }}>
        <EditableGrid
          apiRef={gridApiRef}
          toolbar={() => null}
          height={`calc(90vh - ${LIBRARY_PAGE_HEADER_HEIGHT}px)`}
          processRowUpdate={processRowUpdate}
          rows={sortedRows}
          columns={columns}
          sortField={sortField}
          sortDirection={sortDirection}
          updateSortModel={onSortModelChange}
          sortingMode="server"
          extraActions={extraActions}
          actionsColumnWidth={80}
          loading={isLoading}
          onCellDoubleClick={(params) => {
            // Double-click on the "What" (content) cell opens the details panel
            if (params.field === "content") {
              const row = params.row as MeetingItemRepresentation;
              openItemEditingPanel(row)();
            }
          }}
          isCellEditable={(params) =>
            params.row.isCoachingTask
              ? params.field === "status"
              : params.field !== "dueDate" ||
                !params.row.prioritized ||
                params.row.creator === instance.getActiveAccount()?.username
          }
          highlightedRowId={recentlyEditedRowId}
          dataGridSlotProps={{
            basePopper: {
              sx: {
                "& .MuiPaper-root": {
                  backgroundColor: "white",
                },
              },
            },
          }}
        />
      </Box>

      {/* Success Snackbar */}
      <Snackbar
        open={showSuccessSnackbar}
        autoHideDuration={3500}
        onClose={() => setShowSuccessSnackbar(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setShowSuccessSnackbar(false)}
          severity="success"
          sx={{ width: "100%" }}
        >
          {t("quicktask.edit_own_task.success_message")}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default withApplicationInsights(ActorDashboard, "MyDashboard");
